import gql from 'graphql-tag';
import {flowRight as compose} from 'lodash';
import moment from 'moment';
import PropTypes from 'prop-types';
import React, {useState} from 'react';
import {Mutation} from 'react-apollo';
import {Card, Col, Container, Row} from 'react-bootstrap';
import {useTranslation, withTranslation} from 'react-i18next';
import Input from 'reactstrap/lib/Input';
import styled from 'styled-components';
import Error from '../../../common/components/Error/Error';
import {PulseLoader} from 'react-spinners';
import {
  CancelButton,
  FileDiv,
  FileInput,
  FileLabel, FileNameDiv,
  FooterRow,
  GradientCol,
  TitleButton,
  CenteredP, LoadingContainer, DeleteLogoButton,
} from '../../../common/components/StyledComp';
import Tooltip from '../../../common/components/Tooltip';
import {
  CAMPAIGN_PICTURE_HEIGHT,
  CAMPAIGN_PICTURE_WIDTH,
  IMAGE_MIME_TYPES,
  MAX_CAMPAIGN_CONTENT_LENGTH,
  MAX_CAMPAIGN_TITLE_LENGTH,
} from '../../../common/constants';
import Screen from '../../../common/components/layout/Screen';
import Metadata from '../../../common/components/Metadata';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faBell, faTimes} from '@fortawesome/free-solid-svg-icons';
import DateRangePicker from '../../../common/components/Dates/DateRangePicker';
import isBeforeDay from 'react-dates/lib/utils/isBeforeDay';
import PopIn from '../../../common/components/PopIn';
import {COLORS} from '../../../common/constants';
import {dateFormatFR, numberFormatThousands} from '../../../utils';
import {Alert} from 'reactstrap';

const AttributeDiv = styled.div`
  text-transform: uppercase;
  font-family: 'Segoe UI Bold', sans-serif;
`;

const RowMarginTop = styled(Row)`
  margin-top: 20px;
`;

const CancelButtonForm = styled(CancelButton)`
  margin-top: 0;
  width: auto;
`;

const Span = styled.span`
  font-family: 'Segoe UI Bold', sans-serif;
`;

const MargedAlert = styled(Alert)`
  margin-top: 10px;
`;

const GRAPHQL_MUTATION_ADD_CAMPAIGN = gql`
  mutation addCampaign($campaignInput: AddCampaignInput!) {
    addCampaign(campaignInput: $campaignInput) {
      id
      createdAt
    }
  }
`;

const CreateCampaignView =  ({t, location, history}) => {

  const onDone = (searchQuery) => {
    history.push('/admin/campaign' + (searchQuery ? '?' + searchQuery : ''));
  };

  const [{startDate, endDate}, setDateFilter] = useState({});
  const [newCampaign, setNewCampaign] = useState({
    title: '',
    file: null,
    content: '',
    link: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [fileName, setFileName] = useState('');
  const [wrongFormat, setWrongFormat] = useState(false);
  const [wrongURL, setWrongUrl] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);

  const {t: userT} = useTranslation('user');

  if (!location.state || !location.state.filtersInput || !location.state.summary || !location.state.merchant) {
    console.error('Not a valid state provided to the add campaign view, state provided:', location.state);
    onDone();
    return null;
  }

  const merchant = location.state.merchant;
  const filtersInput = location.state.filtersInput;
  const summary = location.state.summary;
  const campaignInput = {
    ...newCampaign,
    ...filtersInput,
    startDate : startDate ? moment(startDate).format('YYYY-MM-DD') : null,
    endDate : endDate ? moment(endDate).format('YYYY-MM-DD') : null,
  };

  const onFileChange = (event) => {
    setWrongFormat(false);
    const files = event.target.files;
    if (files) {
      if (IMAGE_MIME_TYPES.includes(files[0].type)) {
        setNewCampaign({...newCampaign, file: files[0]});
        setFileName(files[0].name);
      } else {
        setWrongFormat(true);
      }
    }
  };

  const onDeleteFile = event => {
    event.preventDefault();
    event.stopPropagation();
    setFileName('');
    setWrongFormat(false);
    setNewCampaign({
      ...newCampaign,
      file: null,
    });
  };


  const onAskConfirmation = event => {
    event.preventDefault();
    setSubmitted(true);
    setModalVisible(false);
    try {
      if (newCampaign.link) {
        new URL(newCampaign.link);
      }
      setWrongUrl(false);
      if (
        !wrongFormat &&
        campaignInput.title &&
        campaignInput.startDate &&
        campaignInput.endDate &&
        (campaignInput.content || newCampaign.file)
      ) {
        setModalVisible(true);
      }
    } catch (e) {
      setWrongUrl(true);
    }
  };

  const onSubmit = (event, mutation) => {
    event.preventDefault();
    setModalVisible(false);
    mutation({variables: {campaignInput}})
      .then(() => {
        onDone('newCampaign=true');
      })
      .catch(err => {
        console.error(err);
      });
  };

  const displayDate = date => date ? dateFormatFR({date}) : t('campaignField.noDate');
  const displayCivility = civility => civility ? userT(`civilities.${civility}`) : userT(`dataTable.all`);
  const isOutsideDateRange = day => isBeforeDay(day, moment()) || (startDate && isBeforeDay(moment(startDate).add(2, 'weeks'), day));

  return (
    <Screen
      header={<Metadata meta={t('createCampaign.meta')} title={t('createCampaign.title')}/>}
      leftTitle={
        <>
          <FontAwesomeIcon icon={faBell} color={COLORS.PINK_TAG}/>
          <Span className="route-name">{t('campaignField.title')}</Span>
        </>
      }
    >
      <Container fluid>
        <Row>
          <Col lg={12} xl={4}>
            <Card>
              <Card.Header>{t('selectUsers')}</Card.Header>
              <Card.Body>
                <Row>
                  <Col xs={8}>{t('startDate')}</Col>
                  <Col>{displayDate(filtersInput.filterUserStartDate)}</Col>
                </Row>
                <Row>
                  <Col xs={8}>{t('endDate')}</Col>
                  <Col>{displayDate(filtersInput.filterUserEndDate)}</Col>
                </Row>
                <Row>
                  <Col xs={8}>{t('civility')}</Col>
                  <Col>{displayCivility(filtersInput.filterUserCivility)}</Col>
                </Row>
              </Card.Body>
            </Card>
            <Card>
              <Card.Header>{t('reachableClients')}</Card.Header>
              <Card.Body>
                <Row>
                  <Col xs={8}>{t('total')}</Col>
                  <Col>{numberFormatThousands(summary.totalUsers)}</Col>
                </Row>
                <Row>
                  <Col xs={8}>{t('usersWithLoyaltyCard')}</Col>
                  <Col>{numberFormatThousands(summary.totalUsersWithLoyaltyCard)}</Col>
                </Row>
                <Row>
                  <Col xs={8}>{t('usersWithoutLoyaltyCard')}</Col>
                  <Col>{numberFormatThousands(summary.totalUsersWithoutLoyaltyCard)}</Col>
                </Row>
              </Card.Body>
            </Card>
            <Card>
              <Card.Header>{t('createCampaign.notificationTitle')}</Card.Header>
              <Card.Body>
                <Row>
                  <Col xs={8}>{t('availableNotifications')}</Col>
                  <Col>{numberFormatThousands(merchant.totalAvailableNotification)}</Col>
                </Row>
                <Row>
                  <Col xs={8}>{t('createCampaign.notificationLeft')}</Col>
                  <Col>{numberFormatThousands(merchant.totalAvailableNotification - summary.totalUsers)}</Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>

          <Col>
            <Card>
              <Card.Header>{t('campaignField.campaignContent')}</Card.Header>
              <Card.Body>
                <Mutation mutation={GRAPHQL_MUTATION_ADD_CAMPAIGN}>
                  {(mutation, {error, loading}) => {
                    if (loading) {
                      return (
                        <LoadingContainer>
                          <PulseLoader/>
                        </LoadingContainer>
                      );
                    }
                    return (
                      <form>
                        <RowMarginTop>
                          <Col>
                            <AttributeDiv className="label-name">{`${t('campaignField.campaignDates')} : `}</AttributeDiv>
                            <DateRangePicker
                              startDate={startDate}
                              endDate={endDate}
                              isOutsideRange={isOutsideDateRange}
                              onDatesChange={setDateFilter}/>
                            <Tooltip show={(!startDate || !endDate) && submitted} color="red">
                              {t('campaignField.requiredField')}
                            </Tooltip>
                          </Col>
                        </RowMarginTop>
                        <RowMarginTop>
                          <Col>
                            <AttributeDiv className="label-name">{t('campaignField.campaignTitle')} <small>{t('campaignField.limitCharacters', {limit: MAX_CAMPAIGN_TITLE_LENGTH})}</small> :</AttributeDiv>
                            <Input
                              type="text"
                              name="campaignTitle"
                              value={newCampaign.title}
                              onChange={e => {e.target.value.length <= MAX_CAMPAIGN_TITLE_LENGTH && setNewCampaign({...newCampaign, title: e.target.value});}}
                            />
                            <Tooltip show={!newCampaign.title && submitted} color="red">
                              {t('campaignField.requiredField')}
                            </Tooltip>
                          </Col>
                        </RowMarginTop>
                        <RowMarginTop>
                          <Col>
                            <AttributeDiv className="label-name">{t('campaignField.campaignVisual')} <small>{t('campaignField.campaignVisualSize', {width: CAMPAIGN_PICTURE_WIDTH, height: CAMPAIGN_PICTURE_HEIGHT})}</small> :</AttributeDiv>
                            <FileLabel>
                              {' '}
                              <FileNameDiv>{fileName}</FileNameDiv>
                              {fileName && (
                                <DeleteLogoButton onClick={onDeleteFile}>
                                  <FontAwesomeIcon icon={faTimes} color="red" size="sm" />
                                </DeleteLogoButton>
                              )}
                              <FileDiv>{t('campaignField.browse')}</FileDiv>
                              <FileInput
                                type="file"
                                accept={IMAGE_MIME_TYPES.join(',')}
                                onChange={onFileChange}
                              />
                            </FileLabel>
                            <Tooltip show={wrongFormat} color="red">
                              {t('campaignField.wrongFormat')}
                            </Tooltip>
                            <Tooltip show={!newCampaign.content && !newCampaign.file && submitted} color="red">
                              {t('campaignField.requiredFileIfNoContent')}
                            </Tooltip>
                          </Col>
                        </RowMarginTop>
                        <RowMarginTop>
                          <Col>
                            <AttributeDiv className="label-name">{`${t('campaignField.linkToCampaign')} : `}</AttributeDiv>
                            <Input
                              type="url"
                              name="linkToCampaign"
                              placeholder="https://..."
                              onChange={e => {setNewCampaign({...newCampaign, link: e.target.value});}}
                            />
                            <Tooltip show={wrongURL} color="red">
                              {t('campaignField.wrongUrl')}
                            </Tooltip>
                          </Col>
                        </RowMarginTop>
                        <RowMarginTop>
                          <Col>
                            <AttributeDiv className="label-name">{t('campaignField.content')} <small>{t('campaignField.limitCharacters', {limit: MAX_CAMPAIGN_CONTENT_LENGTH})}</small> :</AttributeDiv>
                            <Input
                              type="textarea"
                              name="text"
                              value={newCampaign.content}
                              onChange={e => {e.target.value.length <= MAX_CAMPAIGN_CONTENT_LENGTH && setNewCampaign({...newCampaign, content: e.target.value});}}
                            />
                            <Tooltip show={!newCampaign.content && !newCampaign.file && submitted} color="red">
                              {t('campaignField.requiredContentIfNoFile')}
                            </Tooltip>
                          </Col>
                        </RowMarginTop>

                        <FooterRow>
                          <GradientCol>
                            {(newCampaign.content && newCampaign.file) && (
                              <MargedAlert color="info">{t('campaignField.campaignVisualAlert')}</MargedAlert>
                            )}
                            {error && (
                              <Error error={t('unknownError')}/>
                            )}
                            <div className="popin-buttons">
                              <TitleButton type="submit" onClick={onAskConfirmation}>
                                {t('createCampaign.addCampaign')}
                              </TitleButton>
                              <CancelButtonForm onClick={() => {onDone();}}>
                                {t('cancel')}
                              </CancelButtonForm>
                            </div>
                          </GradientCol>
                        </FooterRow>

                        <PopIn visible={modalVisible}  setModalVisible={setModalVisible} title={t('createCampaign.validateCampaign')}>
                          <Container>
                            <CenteredP>
                              <b>{t('createCampaign.modalTitle')}</b>
                              <br/>
                              <br/>
                              {t('createCampaign.modalContentPart1', {totalUsers: summary.totalUsers, startDate: displayDate(startDate)})}
                              <br/>
                              {t('createCampaign.modalContentPart2')}
                              <br/>
                              {t('createCampaign.modalContentPart3')}
                            </CenteredP>
                            <FooterRow>
                              <GradientCol>
                                <div className="popin-buttons">
                                  <TitleButton type="button" onClick={e => {onSubmit(e, mutation);}}>
                                    {t('createCampaign.validate')}
                                  </TitleButton>
                                  <CancelButtonForm onClick={() => {setModalVisible(false);}}>
                                    {t('cancel')}
                                  </CancelButtonForm>
                                </div>
                              </GradientCol>
                            </FooterRow>
                          </Container>
                        </PopIn>
                      </form>
                    );
                  }}
                </Mutation>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </Screen>
  );

};

CreateCampaignView.propTypes = {
  t: PropTypes.func.isRequired,
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }).isRequired,
  location: PropTypes.shape({
    state: PropTypes.shape({
      summary: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        totalUsers: PropTypes.number.isRequired,
        totalUsersWithLoyaltyCard: PropTypes.number.isRequired,
        totalUsersWithoutLoyaltyCard: PropTypes.number.isRequired,
        canCreate: PropTypes.bool.isRequired,
      }).isRequired,
      filtersInput: PropTypes.shape({
        filterUserStartDate: PropTypes.string,
        filterUserEndDate: PropTypes.string,
        filterUserCivility: PropTypes.string,
      }).isRequired,
      merchant: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        totalAvailableNotification: PropTypes.number.isRequired,
      }).isRequired,
    }).isRequired,
  }).isRequired,
};

export default compose(
  withTranslation('campaign'),
)(CreateCampaignView);
